import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ImagePlus, Send, Paperclip, Loader2, Trash2, Download, Plus, MessageSquare, Library, Palette, X, Check, Pencil, Sparkles,
  CalendarDays, BookOpen, Copy, Star, UserCheck, ChevronLeft, ChevronRight, Link2, RefreshCw, Search,
} from 'lucide-react';
import api from '../../services/api';
import PublicarImagens from './PublicarImagens';

/**
 * Marketing › Criar Imagens.
 *  Chat  — o agente (omni/Antigravity) cria imagens fiéis aos empreendimentos a partir da base, do portal de vendas e do tema ativo.
 *  Base  — imagens dos empreendimentos (fachada, lazer, interior, planta, logo…) com descrição.
 *  Temas — campanhas (ex.: "Corrida pelas vendas" / Fórmula 1): conceito, cores e imagens de exemplo.
 *  Agenda — posts prontos (mídia + legenda + data) das cadências, para revisar, editar e agendar dia após dia.
 *  Calendário — posts por dia, datas comemorativas e banco de pautas (ideias/roteiros de reels).
 *  Perfil — analista de perfil: propõe melhorias de bio, feed, destaques…; cada proposta só vale com a aprovação do gerente.
 *  Conhecimento — memória do agente: localização, entorno e produto de cada empreendimento + Maringá (ele não inventa fora disso).
 */

const CATEGORIAS = [
  ['fachada', 'Fachada'], ['lazer', 'Área de lazer'], ['interior', 'Interior'], ['planta', 'Planta'],
  ['logo', 'Logo'], ['paleta', 'Paleta de cores'], ['outros', 'Outros'],
];
const nomeCategoria = (c) => (CATEGORIAS.find((x) => x[0] === c) || [c, c])[1];
const usuarioAtual = () => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } };
const ehGerente = () => ['admin', 'gerente'].includes(usuarioAtual()?.perfil);
const toast = { error: (m) => window.alert(m), success: () => {} };
const msgErro = (e) => e.response?.data?.error || e.message;

// Realça o termo buscado no trecho (sem diferenciar acento/caixa).
const semAcento = (t) => t.split('').map((ch) => ch.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()[0] || ch).join('');
function Destaque({ texto, termo }) {
  const i = termo ? semAcento(texto).indexOf(semAcento(termo)) : -1;
  if (i < 0) return texto;
  return <>{texto.slice(0, i)}<mark className="bg-amber-100 text-gray-800 rounded px-0.5">{texto.slice(i, i + termo.length)}</mark>{texto.slice(i + termo.length)}</>;
}
const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200';
const btn = 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50';

function baixar(url, nome) {
  const a = document.createElement('a');
  a.href = url; a.download = nome || 'imagem.jpg'; a.target = '_blank'; a.rel = 'noreferrer';
  document.body.appendChild(a); a.click(); a.remove();
}

const selCls = 'border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200';

// Provedor (API) → modelo disponível naquele provedor.
function Escolha({ rotulo, provedores, lista, valor, onChange, provedorAtual }) {
  const prov = provedorAtual || provedores[0]?.id;
  const doProv = lista.filter((x) => x.provedor === prov);
  return (
    <span className="flex items-center gap-1.5">
      <span>{rotulo}:</span>
      <select value={prov || ''} onChange={(e) => onChange((lista.find((x) => x.provedor === e.target.value) || {}).id)} className={selCls} title="Provedor (API)">
        {provedores.filter((p) => lista.some((x) => x.provedor === p.id)).map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
      </select>
      <select value={valor} onChange={(e) => onChange(e.target.value)} className={selCls} title={doProv.find((x) => x.id === valor)?.descricao}>
        {doProv.map((x) => <option key={x.id} value={x.id}>{x.nome}</option>)}
      </select>
    </span>
  );
}

// ─── Chat ───────────────────────────────────────────────────────────────────
function Chat({ prefill, onPrefillUsado, empreendimentos = [] }) {
  const [sel, setSel] = useState({});   // imagens marcadas para legenda/agendamento: id → { id, url, tipo }
  const [publicando, setPublicando] = useState(false);
  const alternar = (i) => setSel((p) => { const n = { ...p }; if (n[i.id]) delete n[i.id]; else n[i.id] = { id: i.id, url: i.url, tipo: i.tipo || 'imagem' }; return n; });
  const marcadas = Object.values(sel);
  const [conversas, setConversas] = useState([]);
  const [atual, setAtual] = useState(null);
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState(null);   // null = sem busca; [] = nada encontrado
  const [foco, setFoco] = useState(null);               // mensagem a destacar ao abrir um resultado
  const [texto, setTexto] = useState('');
  const [anexos, setAnexos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [catalogo, setCatalogo] = useState({ provedores: [], geradores: [{ id: 'auto', nome: 'Automático', provedor: 'omni' }], videos: [], formatos: [] });
  const guardado = (k, p) => { try { return localStorage.getItem(`criarImagens.${k}`) || p; } catch { return p; } };
  const guardar = (k, v) => { try { localStorage.setItem(`criarImagens.${k}`, v); } catch { /* sem storage */ } };
  const [gerador, setGerador] = useState(() => guardado('gerador', 'auto'));
  const [videoModelo, setVideoModelo] = useState(() => guardado('video', 'grok-imagine-video'));
  const [formato, setFormato] = useState(() => guardado('formato', ''));
  const [quantidade, setQuantidade] = useState(1);
  const [logoInbase, setLogoInbase] = useState(() => guardado('logoInbase', '') === '1');
  const [logoEmp, setLogoEmp] = useState(() => guardado('logoEmp', '') === '1');
  useEffect(() => {
    api.get('/marketing/imagens/modelos').then((r) => {
      const c = r.data.data; setCatalogo(c);
      if (!c.geradores.some((g) => g.id === gerador)) setGerador('auto');             // modelo guardado que não existe mais (ex.: chave trocada)
      if (c.videos.length && !c.videos.some((v) => v.id === videoModelo)) setVideoModelo(c.videos[0].id);
    }).catch(() => {});
  }, []);
  const { geradores, videos, provedores, formatos } = catalogo;
  const provDe = (lista, id) => lista.find((x) => x.id === id)?.provedor;
  const escolherGerador = (v) => { setGerador(v); guardar('gerador', v); };
  const escolherVideo = (v) => { setVideoModelo(v); guardar('video', v); };
  const escolherFormato = (v) => { setFormato(v); guardar('formato', v); };
  useEffect(() => { if (prefill) { setTexto(prefill); onPrefillUsado?.(); } }, [prefill, onPrefillUsado]);
  const fim = useRef(null);
  const arq = useRef(null);

  const carregarLista = useCallback(() => api.get('/marketing/imagens/conversas').then((r) => setConversas(r.data.data)).catch(() => {}), []);
  const abrir = useCallback((id) => api.get(`/marketing/imagens/conversas/${id}`).then((r) => setAtual(r.data.data)).catch((e) => toast.error(msgErro(e))), []);
  useEffect(() => { carregarLista(); }, [carregarLista]);

  const processando = atual?.mensagens?.some((m) => m.status === 'processando');
  useEffect(() => {
    if (!processando) return undefined;
    const t = setInterval(() => { abrir(atual.id); }, 3000);
    return () => clearInterval(t);
  }, [processando, atual?.id, abrir]);
  useEffect(() => { if (!processando) carregarLista(); }, [processando, carregarLista]);
  useEffect(() => { if (!foco) fim.current?.scrollIntoView({ behavior: 'smooth' }); }, [atual?.mensagens?.length, processando, foco]);
  useEffect(() => {
    if (!foco || !atual?.mensagens?.some((m) => m.id === foco)) return undefined;
    document.getElementById(`msg-${foco}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => setFoco(null), 4000);
    return () => clearTimeout(t);
  }, [foco, atual]);

  useEffect(() => {
    const q = busca.trim();
    if (q.length < 2) { setResultados(null); return undefined; }
    const t = setTimeout(() => {
      api.get('/marketing/imagens/conversas', { params: { q } }).then((r) => setResultados(r.data.data)).catch((e) => toast.error(msgErro(e)));
    }, 350);
    return () => clearTimeout(t);
  }, [busca]);
  const abrirResultado = (r, mensagemId) => { setFoco(mensagemId || r.mensagem_id); abrir(r.id); };

  const nova = async () => {
    const { data } = await api.post('/marketing/imagens/conversas');
    setAtual({ ...data.data, mensagens: [] }); carregarLista();
  };

  const anexar = async (files) => {
    for (const f of Array.from(files).slice(0, 4 - anexos.length)) {
      const fd = new FormData(); fd.append('imagem', f);
      try { const { data } = await api.post('/marketing/imagens/anexo', fd); setAnexos((a) => [...a, data.data]); } catch (e) { toast.error(msgErro(e)); }
    }
  };

  const enviar = async () => {
    if ((!texto.trim() && !anexos.length) || enviando || processando) return;
    setEnviando(true);
    try {
      let c = atual;
      if (!c) { const { data } = await api.post('/marketing/imagens/conversas'); c = data.data; }
      await api.post(`/marketing/imagens/conversas/${c.id}/mensagens`, { texto, anexos, gerador, video_modelo: videoModelo, formato: formato || undefined, quantidade: quantidade > 1 ? quantidade : undefined, logo_inbase: logoInbase, logo_empreendimento: logoEmp });
      setTexto(''); setAnexos([]);
      await abrir(c.id);
    } catch (e) { toast.error(msgErro(e)); } finally { setEnviando(false); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir esta conversa? As imagens geradas continuam salvas no servidor, mas saem do chat.')) return;
    await api.delete(`/marketing/imagens/conversas/${id}`);
    if (atual?.id === id) setAtual(null);
    carregarLista();
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-14rem)] min-h-[480px]">
      <aside className="w-60 shrink-0 bg-white border border-gray-100 rounded-xl flex flex-col overflow-hidden hidden md:flex">
        <button onClick={nova} className={`${btn} m-3 bg-blue-600 text-white hover:bg-blue-700 justify-center`}><Plus size={16} /> Nova conversa</button>
        <div className="relative mx-3 mb-2">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar conversas e imagens…"
            className="w-full pl-8 pr-7 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
          {busca && <button onClick={() => setBusca('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={14} /></button>}
        </div>
        {resultados ? (
          <div className="flex-1 overflow-y-auto">
            {resultados.map((r) => (
              <div key={r.id} onClick={() => abrirResultado(r)} className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 border-b border-gray-50 ${atual?.id === r.id ? 'bg-blue-50' : ''}`}>
                <div className="flex items-center gap-2 text-gray-800"><MessageSquare size={14} className="shrink-0" /><span className="truncate flex-1 font-medium">{r.titulo}</span></div>
                {r.trecho && <p className="text-xs text-gray-500 mt-1 line-clamp-2"><Destaque texto={r.trecho} termo={busca.trim()} /></p>}
                {r.imagens?.length > 0 && (
                  <div className="flex gap-1 mt-1.5">
                    {r.imagens.map((i) => (i.tipo === 'video'
                      ? <video key={i.id} src={i.url} muted onClick={(e) => { e.stopPropagation(); abrirResultado(r, i.mensagem_id); }} className="h-12 w-12 object-cover rounded" />
                      : <img key={i.id} src={i.url} alt="" loading="lazy" onClick={(e) => { e.stopPropagation(); abrirResultado(r, i.mensagem_id); }} className="h-12 w-12 object-cover rounded" />))}
                  </div>
                )}
                <p className="text-[11px] text-gray-400 mt-1">{new Date(r.updated_at).toLocaleDateString('pt-BR')}{r.ocorrencias > 1 ? ` · ${r.ocorrencias} ocorrências` : ''}</p>
              </div>
            ))}
            {!resultados.length && <p className="text-xs text-gray-400 px-3 py-2">Nada encontrado para “{busca.trim()}”.</p>}
          </div>
        ) : (
        <div className="flex-1 overflow-y-auto">
          {conversas.map((c) => (
            <div key={c.id} onClick={() => abrir(c.id)} className={`group flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${atual?.id === c.id ? 'bg-blue-50 text-blue-800' : 'text-gray-700'}`}>
              <MessageSquare size={14} className="shrink-0" /><span className="truncate flex-1">{c.titulo}</span>
              <button onClick={(e) => { e.stopPropagation(); excluir(c.id); }} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          ))}
          {!conversas.length && <p className="text-xs text-gray-400 px-3 py-2">Nenhuma conversa ainda.</p>}
        </div>
        )}
      </aside>

      <section className="flex-1 bg-white border border-gray-100 rounded-xl flex flex-col overflow-hidden min-w-0">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!atual?.mensagens?.length && (
            <div className="h-full flex flex-col items-center justify-center text-center text-gray-500 gap-2 px-6">
              <Sparkles size={32} className="text-blue-500" />
              <p className="font-medium text-gray-700">O que vamos criar?</p>
              <p className="text-sm max-w-md">Ex.: "Fachada do Lumière vista de drone ao entardecer, com a logo no canto" · "Piscina do Ingarden de dia, com famílias" · "Post de campanha no tema ativo para o Lumière".</p>
            </div>
          )}
          {atual?.mensagens?.map((m) => (
            <div key={m.id} id={`msg-${m.id}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm transition-shadow ${foco === m.id ? 'ring-4 ring-amber-300' : ''} ${m.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-800 border border-gray-100'}`}>
                {m.anexos?.length > 0 && <div className="flex gap-2 mb-2 flex-wrap">{m.anexos.map((a) => <img key={a.id} src={a.url} alt="" className="h-20 rounded-lg" />)}</div>}
                {m.status === 'processando' && <div className="flex items-center gap-2 text-gray-500"><Loader2 size={16} className="animate-spin" /> Analisando a base e gerando… (imagem: 30 s a 2 min · vídeo: alguns minutos)</div>}
                {m.texto && <p className="whitespace-pre-wrap">{m.texto.replace(/\*\*/g, '')}</p>}
                {m.status === 'erro' && <p className="text-red-600">Erro: {m.erro}</p>}
                {m.imagens?.length > 0 && (
                  <div className="mt-3 grid gap-3">
                    {m.imagens.map((i) => (
                      <div key={i.id} className="relative group">
                        <label className="absolute top-2 left-2 z-10 bg-white/90 hover:bg-white rounded-lg px-2 py-1.5 shadow text-xs text-gray-700 flex items-center gap-1.5 cursor-pointer" title="Selecione para criar legenda e agendar">
                          <input type="checkbox" checked={!!sel[i.id]} onChange={() => alternar(i)} /> Selecionar
                        </label>
                        {i.tipo === 'video'
                          ? <video src={i.url} controls playsInline className="rounded-xl max-h-[520px] w-auto border border-gray-200" />
                          : <img src={i.url} alt="Imagem gerada" className="rounded-xl max-h-[520px] w-auto border border-gray-200" />}
                        <button onClick={() => baixar(i.url, `inbase-${i.id.split(':')[1]}`)} title="Baixar" className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-lg p-2 shadow text-gray-700"><Download size={16} /></button>
                      </div>
                    ))}
                  </div>
                )}
                {m.role === 'assistant' && m.modelo && m.status === 'ok' && <p className="text-[10px] text-gray-400 mt-2">{m.modelo}</p>}
              </div>
            </div>
          ))}
          <div ref={fim} />
        </div>

        {marcadas.length > 0 && (
          <div className="border-t border-blue-100 bg-blue-50 px-3 py-2 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-blue-800 font-medium">{marcadas.length} {marcadas.length === 1 ? 'selecionada' : 'selecionadas'}</span>
            <button onClick={() => setPublicando(true)} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}><Sparkles size={14} /> Criar legenda e agendar</button>
            <button onClick={() => setSel({})} className={`${btn} text-gray-600 hover:bg-white`}>Limpar</button>
          </div>
        )}
        <div className="border-t border-gray-100 p-3">
          {anexos.length > 0 && (
            <div className="flex gap-2 mb-2">{anexos.map((a) => (
              <div key={a.id} className="relative"><img src={a.url} alt="" className="h-14 rounded-lg" />
                <button onClick={() => setAnexos((x) => x.filter((y) => y.id !== a.id))} className="absolute -top-1.5 -right-1.5 bg-gray-700 text-white rounded-full p-0.5"><X size={12} /></button></div>
            ))}</div>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-2 text-xs text-gray-500">
            <Escolha rotulo="Imagem" provedores={provedores} lista={geradores} valor={gerador} onChange={escolherGerador} provedorAtual={provDe(geradores, gerador)} />
            {videos.length > 0 && <Escolha rotulo="Vídeo" provedores={provedores} lista={videos} valor={videoModelo} onChange={escolherVideo} provedorAtual={provDe(videos, videoModelo)} />}
            {catalogo.higgsfield_conectado === false && <a href="/configuracoes/integracoes" className="text-blue-600 underline">Conectar Higgsfield</a>}
            {catalogo.kling_conectado === false && <a href="/configuracoes/integracoes" className="text-blue-600 underline">Conectar Kling</a>}
            <label className="flex items-center gap-1.5">Formato
              <select value={formato} onChange={(e) => escolherFormato(e.target.value)} className={selCls}>
                <option value="">Automático (o agente escolhe)</option>
                {formatos.map((f) => <option key={f.nome} value={f.nome}>{f.nome}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer" title="Aplica o logo da inBase no canto inferior esquerdo">
              <input type="checkbox" checked={logoInbase} onChange={(e) => { setLogoInbase(e.target.checked); guardar('logoInbase', e.target.checked ? '1' : ''); }} /> Logo da inBase
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer" title="Aplica o logo do empreendimento no canto inferior direito">
              <input type="checkbox" checked={logoEmp} onChange={(e) => { setLogoEmp(e.target.checked); guardar('logoEmp', e.target.checked ? '1' : ''); }} /> Logo do empreendimento
            </label>
            <label className="flex items-center gap-1.5" title="Cadência: quantos posts o agente deve entregar">Posts
              <select value={quantidade} onChange={(e) => setQuantidade(Number(e.target.value))} className={selCls}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12].map((n) => <option key={n} value={n}>{n === 1 ? '1 (único)' : `${n} (cadência)`}</option>)}
              </select>
            </label>
          </div>
          <div className="flex items-end gap-2">
            <input ref={arq} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { anexar(e.target.files); e.target.value = ''; }} />
            <button onClick={() => arq.current?.click()} title="Anexar imagem de referência" className={`${btn} text-gray-600 hover:bg-gray-100`}><Paperclip size={18} /></button>
            <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={2} placeholder="Descreva a imagem (ou o vídeo) que você quer…"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }} className={`${inp} resize-none`} />
            <button onClick={enviar} disabled={enviando || processando || (!texto.trim() && !anexos.length)} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>
              {enviando || processando ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            </button>
          </div>
        </div>
      </section>
      {publicando && <PublicarImagens imagens={marcadas} empreendimentos={empreendimentos} onClose={() => setPublicando(false)} />}
    </div>
  );
}

// ─── Formulário de upload (base e temas) ────────────────────────────────────
function Upload({ empreendimentos, fixo, onFeito }) {
  const [f, setF] = useState({ titulo: '', descricao: '', categoria: fixo?.categoria || 'fachada', empreendimento_id: '' });
  const [arquivo, setArquivo] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const ehTema = !!fixo?.tema_id;

  const enviar = async () => {
    if (!arquivo || !f.titulo.trim()) return toast.error('Escolha a imagem e dê um título');
    const fd = new FormData();
    fd.append('imagem', arquivo); fd.append('titulo', f.titulo); fd.append('descricao', f.descricao);
    fd.append('categoria', ehTema ? 'tema' : f.categoria);
    if (ehTema) fd.append('tema_id', fixo.tema_id); else if (f.empreendimento_id) fd.append('empreendimento_id', f.empreendimento_id);
    setSalvando(true);
    try {
      await api.post('/marketing/imagens/base', fd);
      setF({ ...f, titulo: '', descricao: '' }); setArquivo(null); onFeito();
      toast.success('Imagem adicionada');
    } catch (e) { toast.error(msgErro(e)); } finally { setSalvando(false); }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 grid gap-3 md:grid-cols-2">
      <input type="file" accept="image/*" onChange={(e) => setArquivo(e.target.files[0] || null)} className="text-sm md:col-span-2" />
      <input className={inp} placeholder="Título (ex.: Piscina principal)" value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} />
      {!ehTema && (
        <div className="flex gap-2">
          <select className={inp} value={f.empreendimento_id} onChange={(e) => setF({ ...f, empreendimento_id: e.target.value })}>
            <option value="">Institucional inBase</option>
            {empreendimentos.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
          </select>
          <select className={inp} value={f.categoria} onChange={(e) => setF({ ...f, categoria: e.target.value })}>
            {CATEGORIAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      )}
      <textarea className={`${inp} md:col-span-2`} rows={2} placeholder="Descrição — o que é, ângulo, materiais, cores, detalhes que a IA deve respeitar" value={f.descricao} onChange={(e) => setF({ ...f, descricao: e.target.value })} />
      <div className="md:col-span-2"><button onClick={enviar} disabled={salvando} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>{salvando ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} Adicionar</button></div>
    </div>
  );
}

function CartaoImagem({ img, onMudou }) {
  const [edit, setEdit] = useState(false);
  const [t, setT] = useState(img.titulo);
  const [d, setD] = useState(img.descricao || '');
  const salvar = async () => { try { await api.put(`/marketing/imagens/base/${img.id}`, { titulo: t, descricao: d }); setEdit(false); onMudou(); } catch (e) { toast.error(msgErro(e)); } };
  const excluir = async () => { if (!window.confirm(`Excluir "${img.titulo}"?`)) return; try { await api.delete(`/marketing/imagens/base/${img.id}`); onMudou(); } catch (e) { toast.error(msgErro(e)); } };
  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden flex flex-col">
      <div className="bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] aspect-[4/3] flex items-center justify-center">
        <img src={img.url} alt={img.titulo} className="max-h-full max-w-full object-contain" loading="lazy" />
      </div>
      <div className="p-3 text-sm flex-1 flex flex-col gap-1">
        {edit ? (<>
          <input className={inp} value={t} onChange={(e) => setT(e.target.value)} />
          <textarea className={inp} rows={3} value={d} onChange={(e) => setD(e.target.value)} />
          <div className="flex gap-2"><button onClick={salvar} className={`${btn} bg-blue-600 text-white`}><Check size={14} /> Salvar</button><button onClick={() => setEdit(false)} className={`${btn} text-gray-600`}>Cancelar</button></div>
        </>) : (<>
          <p className="font-medium text-gray-800">{img.titulo}</p>
          <p className="text-xs text-gray-500">{img.empreendimento_nome || (img.tema_nome ? `Tema: ${img.tema_nome}` : 'Institucional')} · {nomeCategoria(img.categoria)}</p>
          {img.descricao && <p className="text-xs text-gray-600 line-clamp-3">{img.descricao}</p>}
          <div className="flex gap-3 mt-auto pt-2 text-gray-400">
            <button onClick={() => setEdit(true)} title="Editar" className="hover:text-blue-600"><Pencil size={15} /></button>
            <button onClick={excluir} title="Excluir" className="hover:text-red-600"><Trash2 size={15} /></button>
          </div>
        </>)}
      </div>
    </div>
  );
}

// ─── Base ───────────────────────────────────────────────────────────────────
function Base({ empreendimentos }) {
  const [itens, setItens] = useState([]);
  const [emp, setEmp] = useState('');
  const [cat, setCat] = useState('');
  const carregar = useCallback(() => {
    const params = {}; if (emp) params.empreendimento_id = emp; if (cat) params.categoria = cat;
    api.get('/marketing/imagens/base', { params }).then((r) => setItens(r.data.data.filter((i) => !i.tema_id))).catch((e) => toast.error(msgErro(e)));
  }, [emp, cat]);
  useEffect(() => { carregar(); }, [carregar]);
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">Quanto melhor a descrição, mais fiel fica a imagem criada. O agente também consulta sozinho as fotos e plantas do portal de vendas e os dados dos empreendimentos.</p>
      <Upload empreendimentos={empreendimentos} onFeito={carregar} />
      <div className="flex gap-2">
        <select className={`${inp} max-w-[240px]`} value={emp} onChange={(e) => setEmp(e.target.value)}>
          <option value="">Todos os empreendimentos</option><option value="institucional">Institucional inBase</option>
          {empreendimentos.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
        <select className={`${inp} max-w-[200px]`} value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">Todas as categorias</option>{CATEGORIAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
        {itens.map((i) => <CartaoImagem key={i.id} img={i} onMudou={carregar} />)}
      </div>
      {!itens.length && <p className="text-sm text-gray-400">Nada por aqui ainda.</p>}
    </div>
  );
}

// ─── Temas ──────────────────────────────────────────────────────────────────
function Temas() {
  const [temas, setTemas] = useState([]);
  const [form, setForm] = useState(null);
  const carregar = useCallback(() => api.get('/marketing/imagens/temas').then((r) => setTemas(r.data.data)).catch((e) => toast.error(msgErro(e))), []);
  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    try {
      if (form.id) await api.put(`/marketing/imagens/temas/${form.id}`, form); else await api.post('/marketing/imagens/temas', form);
      setForm(null); carregar();
    } catch (e) { toast.error(msgErro(e)); }
  };
  const ativar = async (t) => { await api.post(`/marketing/imagens/temas/${t.id}/ativar`, { ativo: !t.ativo }); carregar(); };
  const excluir = async (t) => { if (!window.confirm(`Excluir o tema "${t.nome}" e as imagens de exemplo dele?`)) return; try { await api.delete(`/marketing/imagens/temas/${t.id}`); carregar(); } catch (e) { toast.error(msgErro(e)); } };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Tema = campanha em andamento. Cadastre o conceito, as cores e imagens de exemplo; o tema ativo é aplicado pelo agente nos pedidos de campanha.</p>
        <button onClick={() => setForm({ nome: '', descricao: '', cores: '' })} className={`${btn} bg-blue-600 text-white hover:bg-blue-700 shrink-0`}><Plus size={16} /> Novo tema</button>
      </div>
      {form && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 grid gap-3">
          <input className={inp} placeholder="Nome (ex.: Corrida pelas vendas — Fórmula 1)" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
          <textarea className={inp} rows={3} placeholder="Conceito visual: estilo, elementos (bandeira quadriculada, pit stop, pista…), clima, tipografia, o que evitar" value={form.descricao || ''} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
          <input className={inp} placeholder="Cores da campanha (ex.: vermelho #D91E18, preto #111111, branco, amarelo #FFD400)" value={form.cores || ''} onChange={(e) => setForm({ ...form, cores: e.target.value })} />
          <div className="flex gap-2"><button onClick={salvar} className={`${btn} bg-blue-600 text-white`}><Check size={16} /> Salvar</button><button onClick={() => setForm(null)} className={`${btn} text-gray-600`}>Cancelar</button></div>
        </div>
      )}
      {temas.map((t) => (
        <div key={t.id} className={`bg-white border rounded-xl p-4 space-y-3 ${t.ativo ? 'border-green-300 ring-1 ring-green-200' : 'border-gray-100'}`}>
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 flex items-center gap-2">{t.nome}{t.ativo && <span className="text-[11px] font-medium bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Tema ativo</span>}</p>
              {t.descricao && <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{t.descricao}</p>}
              {t.cores && <p className="text-xs text-gray-500 mt-1">Cores: {t.cores}</p>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => ativar(t)} className={`${btn} ${t.ativo ? 'bg-gray-100 text-gray-700' : 'bg-green-600 text-white'}`}>{t.ativo ? 'Desativar' : 'Ativar'}</button>
              <button onClick={() => setForm(t)} title="Editar" className={`${btn} text-gray-500 hover:text-blue-600`}><Pencil size={16} /></button>
              <button onClick={() => excluir(t)} title="Excluir" className={`${btn} text-gray-500 hover:text-red-600`}><Trash2 size={16} /></button>
            </div>
          </div>
          <div className="grid gap-4 grid-cols-2 md:grid-cols-4 xl:grid-cols-5">{t.imagens.map((i) => <CartaoImagem key={i.id} img={{ ...i, tema_nome: t.nome }} onMudou={carregar} />)}</div>
          <Upload empreendimentos={[]} fixo={{ tema_id: t.id }} onFeito={carregar} />
        </div>
      ))}
      {!temas.length && !form && <p className="text-sm text-gray-400">Nenhum tema cadastrado.</p>}
    </div>
  );
}

// ─── Agenda (posts prontos) ─────────────────────────────────────────────────
// rascunho → aguardando aprovação → aprovado (só gerente) → agendado → publicado. Nada é publicado pelo sistema: você posta e marca aqui.
const STATUS_POST = [['rascunho', 'Rascunho'], ['aguardando_aprovacao', 'Aguardando aprovação'], ['aprovado', 'Aprovado'], ['agendado', 'Agendado'], ['publicado', 'Publicado'], ['cancelado', 'Cancelado']];
const COR_STATUS = { rascunho: 'bg-gray-100 text-gray-600', aguardando_aprovacao: 'bg-amber-100 text-amber-700', aprovado: 'bg-green-100 text-green-700', agendado: 'bg-blue-100 text-blue-700', publicado: 'bg-purple-100 text-purple-700', cancelado: 'bg-red-100 text-red-600' };
const METRICAS = [['alcance', 'Alcance'], ['curtidas', 'Curtidas'], ['comentarios', 'Coment.'], ['salvamentos', 'Salvos'], ['compartilhamentos', 'Compart.'], ['cliques', 'Cliques']];
const paraInputLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso); const z = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`;
};

function CartaoPost({ post, onMudou, onAdaptar }) {
  const [t, setT] = useState(post.titulo || '');
  const [l, setL] = useState(post.legenda || '');
  const [h, setH] = useState(post.hashtags || '');
  const [d, setD] = useState(paraInputLocal(post.agendado_para));
  const [m, setM] = useState(post.metricas || {});
  const [salvando, setSalvando] = useState(false);
  const [copiado, setCopiado] = useState('');
  const [aberto, setAberto] = useState(false);
  const sujo = t !== (post.titulo || '') || l !== (post.legenda || '') || h !== (post.hashtags || '') || d !== paraInputLocal(post.agendado_para) || JSON.stringify(m) !== JSON.stringify(post.metricas || {});

  const enviar = async (corpo, aviso) => {
    setSalvando(true);
    try { await api.put(`/marketing/imagens/posts/${post.id}`, corpo); onMudou(); } catch (e) { toast.error(msgErro(e)); } finally { setSalvando(false); }
    if (aviso) toast.success(aviso);
  };
  const salvar = () => enviar({ titulo: t, legenda: l, hashtags: h, agendado_para: d ? new Date(d).toISOString() : null, metricas: m });
  const excluir = async () => { if (!window.confirm('Excluir este post da agenda?')) return; try { await api.delete(`/marketing/imagens/posts/${post.id}`); onMudou(); } catch (e) { toast.error(msgErro(e)); } };
  const copiar = async (txt, chave) => { try { await navigator.clipboard.writeText(txt); setCopiado(chave); setTimeout(() => setCopiado(''), 1800); } catch { toast.error('Não foi possível copiar'); } };
  const st = post.status;

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 flex gap-4 flex-col md:flex-row">
      <div className="flex md:flex-col gap-2 shrink-0 md:w-44">
        {(post.midia || []).map((x) => (
          <div key={x.id} className="relative group">
            {x.tipo === 'video' ? <video src={x.url} controls playsInline className="rounded-lg w-full" /> : <img src={x.url} alt="" className="rounded-lg w-full" loading="lazy" />}
            <button onClick={() => baixar(x.url, `inbase-${x.id.split(':')[1]}`)} title="Baixar" className="absolute top-1.5 right-1.5 bg-white/90 hover:bg-white rounded-md p-1.5 shadow text-gray-700"><Download size={14} /></button>
          </div>
        ))}
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
          <span className={`px-2 py-0.5 rounded-full font-medium ${COR_STATUS[st] || COR_STATUS.rascunho}`}>{(STATUS_POST.find((x) => x[0] === st) || [st, st])[1]}</span>
          {post.ordem != null && <span className="font-semibold text-blue-700">#{post.ordem}</span>}
          {post.empreendimento_nome && <span>{post.empreendimento_nome}</span>}
          {post.formato && <span>· {post.formato}</span>}
          {post.publicacao_auto && post.conta_nome && <span className="text-pink-600">· Publicação: {post.conta_nome}</span>}
          {post.erro_publicacao && <span className="text-red-600" title={post.erro_publicacao}>· falhou ao publicar: {post.erro_publicacao.slice(0, 80)}</span>}
          {post.aprovado_por && <span>· aprovado por {post.aprovado_por}</span>}
          <span className="ml-auto flex items-center gap-1" title="Leads do CRM com utm_content deste post">{post.leads} lead{post.leads === 1 ? '' : 's'}</span>
          <button onClick={() => enviar({ destaque: !post.destaque })} title="Destaque: entra na biblioteca de legendas que o agente usa como referência" className={post.destaque ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'}><Star size={16} fill={post.destaque ? 'currentColor' : 'none'} /></button>
        </div>
        <input className={inp} value={t} onChange={(e) => setT(e.target.value)} placeholder="Título interno" />
        <textarea className={inp} rows={6} value={l} onChange={(e) => setL(e.target.value)} placeholder="Legenda" />
        <input className={inp} value={h} onChange={(e) => setH(e.target.value)} placeholder="#hashtags" />
        <div className="flex flex-wrap items-center gap-2">
          <input type="datetime-local" className={`${inp} max-w-[220px]`} value={d} onChange={(e) => setD(e.target.value)} title="Data/hora sugerida para postar" />
          <button onClick={() => copiar(`${l}${h ? `\n\n${h}` : ''}`, 'leg')} className={`${btn} text-gray-600 hover:bg-gray-100`}><Copy size={14} /> {copiado === 'leg' ? 'Copiado!' : 'Copiar legenda'}</button>
          {post.link_utm && <button onClick={() => copiar(post.link_utm, 'utm')} title={post.link_utm} className={`${btn} text-gray-600 hover:bg-gray-100`}><Link2 size={14} /> {copiado === 'utm' ? 'Copiado!' : 'Link rastreado'}</button>}
          <button onClick={() => onAdaptar(post)} className={`${btn} text-gray-600 hover:bg-gray-100`} title="Pedir ao agente uma versão para outro formato">Adaptar formato</button>
          <button onClick={salvar} disabled={!sujo || salvando} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>{salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar</button>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100">
          <span className="text-xs text-gray-500">Fluxo:</span>
          {(st === 'rascunho') && <button onClick={() => enviar({ status: 'aguardando_aprovacao' })} className={`${btn} bg-amber-500 text-white hover:bg-amber-600`}>Enviar para aprovação</button>}
          {(st === 'aguardando_aprovacao') && (ehGerente()
            ? <><button onClick={() => enviar({ status: 'aprovado' })} className={`${btn} bg-green-600 text-white hover:bg-green-700`}><Check size={14} /> Aprovar</button><button onClick={() => enviar({ status: 'rascunho' })} className={`${btn} text-gray-600 hover:bg-gray-100`}>Devolver p/ rascunho</button></>
            : <span className="text-xs text-amber-700">Aguardando um gerente aprovar</span>)}
          {st === 'aprovado' && <button onClick={() => enviar({ status: 'agendado' })} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>Marcar como agendado</button>}
          {['aprovado', 'agendado'].includes(st) && <button onClick={() => enviar({ status: 'publicado' })} className={`${btn} bg-purple-600 text-white hover:bg-purple-700`}>Marcar como publicado</button>}
          {['aprovado', 'agendado', 'publicado'].includes(st) && <button onClick={() => enviar({ status: 'rascunho' })} className={`${btn} text-gray-500 hover:bg-gray-100`}>Reabrir</button>}
          {st !== 'cancelado' && <button onClick={() => enviar({ status: 'cancelado' })} className={`${btn} text-gray-400 hover:text-red-600`}>Cancelar</button>}
          <button onClick={excluir} title="Excluir" className={`${btn} text-gray-400 hover:text-red-600 ml-auto`}><Trash2 size={15} /></button>
        </div>
        <button onClick={() => setAberto(!aberto)} className="text-xs text-blue-600 hover:underline">{aberto ? 'Ocultar' : 'Registrar'} métricas do post</button>
        {aberto && (
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {METRICAS.map(([k, n]) => (
              <label key={k} className="text-[11px] text-gray-500">{n}
                <input type="number" min="0" className={inp} value={m[k] ?? ''} onChange={(e) => setM({ ...m, [k]: e.target.value === '' ? undefined : Number(e.target.value) })} />
              </label>
            ))}
            <p className="col-span-full text-[11px] text-gray-400">Copie do Instagram Insights e clique em Salvar. Posts com bons números (ou marcados com ★) viram referência de tom para as próximas legendas.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Agenda({ onAdaptar }) {
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState('');
  const [campanha, setCampanha] = useState('');
  const carregar = useCallback(() => {
    api.get('/marketing/imagens/posts', { params: status ? { status } : {} }).then((r) => setPosts(r.data.data)).catch((e) => toast.error(msgErro(e)));
  }, [status]);
  useEffect(() => { carregar(); }, [carregar]);
  const campanhas = [...new Set(posts.map((p) => p.campanha).filter(Boolean))];
  const lista = campanha ? posts.filter((p) => p.campanha === campanha) : posts;
  const fmtDia = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' }) : 'Sem data');
  const dias = [...new Set(lista.map((p) => fmtDia(p.agendado_para)))];
  const pendentes = posts.filter((p) => p.status === 'aguardando_aprovacao').length;
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">Posts criados pelo agente (imagem + legenda + data sugerida). <b>Nada é publicado nem agendado automaticamente</b>: o post nasce "aguardando aprovação", o gerente aprova, e você posta no Instagram/Facebook e marca como publicado.</p>
      {pendentes > 0 && <p className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">{pendentes} post{pendentes > 1 ? 's' : ''} aguardando aprovação.</p>}
      <div className="flex gap-2 flex-wrap">
        <select className={`${inp} max-w-[220px]`} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Todos (menos cancelados)</option>{STATUS_POST.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
        </select>
        <select className={`${inp} max-w-[280px]`} value={campanha} onChange={(e) => setCampanha(e.target.value)}>
          <option value="">Todas as campanhas</option>{campanhas.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {dias.map((dia) => (
        <div key={dia} className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 capitalize flex items-center gap-2"><CalendarDays size={15} /> {dia}</h3>
          {lista.filter((p) => fmtDia(p.agendado_para) === dia).map((p) => <CartaoPost key={`${p.id}-${p.updated_at}`} post={p} onMudou={carregar} onAdaptar={onAdaptar} />)}
        </div>
      ))}
      {!lista.length && <p className="text-sm text-gray-400">Nenhum post ainda. Peça no chat uma imagem com legenda ou uma cadência.</p>}
    </div>
  );
}

// ─── Calendário editorial + banco de pautas ─────────────────────────────────
const NOMES_MES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const chaveDia = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// n-ésimo dia da semana (0=dom) do mês; n=-1 → último
const nesimo = (ano, mes, dow, n) => { const d = new Date(ano, mes, 1); const dias = []; while (d.getMonth() === mes) { if (d.getDay() === dow) dias.push(new Date(d)); d.setDate(d.getDate() + 1); } return n < 0 ? dias[dias.length - 1] : dias[n - 1]; };

function datasComemorativas(ano) {
  const l = [
    [new Date(ano, 0, 1), 'Ano Novo'], [new Date(ano, 2, 8), 'Dia da Mulher'], [new Date(ano, 2, 15), 'Dia do Consumidor'],
    [new Date(ano, 4, 1), 'Dia do Trabalhador'], [new Date(ano, 4, 10), 'Aniversário de Maringá'], [nesimo(ano, 4, 0, 2), 'Dia das Mães'],
    [new Date(ano, 5, 12), 'Dia dos Namorados'], [nesimo(ano, 7, 0, 2), 'Dia dos Pais'], [new Date(ano, 7, 27), 'Dia do Corretor de Imóveis'],
    [new Date(ano, 8, 15), 'Dia do Cliente'], [new Date(ano, 9, 12), 'Dia das Crianças'], [new Date(ano, 9, 15), 'Dia do Professor'],
    [nesimo(ano, 10, 5, 4), 'Black Friday'], [new Date(ano, 11, 24), 'Véspera de Natal'], [new Date(ano, 11, 25), 'Natal'], [new Date(ano, 11, 31), 'Réveillon'],
  ];
  return Object.fromEntries(l.map(([d, n]) => [chaveDia(d), n]));
}
const TIPOS_PAUTA = [['post', 'Post'], ['reels', 'Reels'], ['stories', 'Stories'], ['carrossel', 'Carrossel']];

function Calendario({ empreendimentos, onCriarNoChat }) {
  const hoje = new Date();
  const [ref, setRef] = useState(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
  const [posts, setPosts] = useState([]);
  const [pautas, setPautas] = useState([]);
  const [form, setForm] = useState(null);
  const carregar = useCallback(() => {
    api.get('/marketing/imagens/posts').then((r) => setPosts(r.data.data)).catch(() => {});
    api.get('/marketing/imagens/pautas').then((r) => setPautas(r.data.data)).catch(() => {});
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const datas = datasComemorativas(ref.getFullYear());
  const primeiro = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const vazios = primeiro.getDay();
  const nDias = new Date(ref.getFullYear(), ref.getMonth() + 1, 0).getDate();
  const porDia = {};
  posts.filter((p) => p.agendado_para).forEach((p) => { const k = chaveDia(new Date(p.agendado_para)); (porDia[k] = porDia[k] || []).push(p); });
  const pautaPorDia = {};
  pautas.filter((p) => p.data_sugerida).forEach((p) => { const k = String(p.data_sugerida).slice(0, 10); (pautaPorDia[k] = pautaPorDia[k] || []).push(p); });

  const salvar = async () => {
    try {
      if (form.id) await api.put(`/marketing/imagens/pautas/${form.id}`, form); else await api.post('/marketing/imagens/pautas', form);
      setForm(null); carregar();
    } catch (e) { toast.error(msgErro(e)); }
  };
  const mudar = async (p, status) => { try { await api.put(`/marketing/imagens/pautas/${p.id}`, { ...p, status, data_sugerida: p.data_sugerida ? String(p.data_sugerida).slice(0, 10) : '' }); carregar(); } catch (e) { toast.error(msgErro(e)); } };
  const excluir = async (p) => { if (!window.confirm(`Excluir a pauta "${p.titulo}"?`)) return; try { await api.delete(`/marketing/imagens/pautas/${p.id}`); carregar(); } catch (e) { toast.error(msgErro(e)); } };

  const ProximasDatas = Object.entries(datas).filter(([k]) => k >= chaveDia(hoje)).slice(0, 4);
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500">Posts por dia (pela data sugerida), datas comemorativas e o banco de pautas. Peça ao agente no chat "5 ideias de reels para o Dia das Mães" e elas caem aqui para você aprovar.</p>
      <div className="bg-white border border-gray-100 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setRef(new Date(ref.getFullYear(), ref.getMonth() - 1, 1))} className="text-gray-500 hover:text-gray-800"><ChevronLeft size={18} /></button>
          <h3 className="font-semibold text-gray-800">{NOMES_MES[ref.getMonth()]} {ref.getFullYear()}</h3>
          <button onClick={() => setRef(new Date(ref.getFullYear(), ref.getMonth() + 1, 1))} className="text-gray-500 hover:text-gray-800"><ChevronRight size={18} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-[11px] text-gray-400 mb-1">{['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((d) => <div key={d} className="text-center">{d}</div>)}</div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: vazios }).map((_, i) => <div key={`v${i}`} />)}
          {Array.from({ length: nDias }).map((_, i) => {
            const dia = new Date(ref.getFullYear(), ref.getMonth(), i + 1); const k = chaveDia(dia);
            const ps = porDia[k] || []; const pt = pautaPorDia[k] || [];
            return (
              <div key={k} className={`min-h-[84px] border rounded-lg p-1 text-[11px] ${k === chaveDia(hoje) ? 'border-blue-400 bg-blue-50/40' : 'border-gray-100'}`}>
                <div className="text-gray-500 font-medium">{i + 1}</div>
                {datas[k] && <div className="text-[10px] text-rose-600 font-medium leading-tight">{datas[k]}</div>}
                {ps.map((p) => <div key={p.id} title={p.titulo} className={`truncate rounded px-1 mt-0.5 ${COR_STATUS[p.status] || ''}`}>{p.titulo || 'Post'}</div>)}
                {pt.map((p) => <div key={p.id} title={p.titulo} className="truncate rounded px-1 mt-0.5 bg-indigo-50 text-indigo-700">💡 {p.titulo}</div>)}
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-gray-400 mt-2">Datas móveis (Dia das Mães, Dia dos Pais, Black Friday) são calculadas; confira antes de planejar.</p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold text-gray-800">Banco de pautas {ProximasDatas.length > 0 && <span className="text-xs font-normal text-gray-400">· próximas datas: {ProximasDatas.map(([k, n]) => `${n} (${k.slice(8)}/${k.slice(5, 7)})`).join(', ')}</span>}</h3>
        <button onClick={() => setForm({ tipo: 'reels', titulo: '', roteiro: '', empreendimento_id: '', data_sugerida: '' })} className={`${btn} bg-blue-600 text-white hover:bg-blue-700 shrink-0`}><Plus size={16} /> Nova pauta</button>
      </div>
      {form && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 grid gap-3">
          <div className="flex gap-2 flex-wrap">
            <select className={`${inp} max-w-[150px]`} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>{TIPOS_PAUTA.map(([v, n]) => <option key={v} value={v}>{n}</option>)}</select>
            <select className={`${inp} max-w-[240px]`} value={form.empreendimento_id || ''} onChange={(e) => setForm({ ...form, empreendimento_id: e.target.value })}><option value="">Sem empreendimento</option>{empreendimentos.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}</select>
            <input type="date" className={`${inp} max-w-[180px]`} value={form.data_sugerida ? String(form.data_sugerida).slice(0, 10) : ''} onChange={(e) => setForm({ ...form, data_sugerida: e.target.value })} />
          </div>
          <input className={inp} placeholder="Título da ideia" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} />
          <textarea className={inp} rows={6} placeholder="Roteiro / cenas / texto de apoio / chamada para ação" value={form.roteiro || ''} onChange={(e) => setForm({ ...form, roteiro: e.target.value })} />
          <div className="flex gap-2"><button onClick={salvar} className={`${btn} bg-blue-600 text-white`}><Check size={16} /> Salvar</button><button onClick={() => setForm(null)} className={`${btn} text-gray-600`}>Cancelar</button></div>
        </div>
      )}
      {pautas.map((p) => (
        <div key={p.id} className="bg-white border border-gray-100 rounded-xl p-4">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-800 flex items-center gap-2 flex-wrap">
                <span className="text-[11px] uppercase tracking-wide bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">{(TIPOS_PAUTA.find((x) => x[0] === p.tipo) || [p.tipo, p.tipo])[1]}</span>
                {p.titulo}
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${p.status === 'aprovada' ? 'bg-green-100 text-green-700' : p.status === 'usada' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
              </p>
              <p className="text-xs text-gray-400">{[p.empreendimento_nome, p.data_sugerida && new Date(`${String(p.data_sugerida).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR'), p.origem === 'agente' ? 'sugerida pelo agente' : null].filter(Boolean).join(' · ')}</p>
            </div>
            <div className="flex gap-1 shrink-0 flex-wrap">
              {p.status === 'ideia' && ehGerente() && <button onClick={() => mudar(p, 'aprovada')} className={`${btn} bg-green-600 text-white hover:bg-green-700`}>Aprovar</button>}
              {p.status === 'aprovada' && <button onClick={() => onCriarNoChat(p)} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>Criar no chat</button>}
              {p.status !== 'usada' && <button onClick={() => mudar(p, 'usada')} className={`${btn} text-gray-500 hover:bg-gray-100`}>Marcar usada</button>}
              <button onClick={() => setForm({ ...p, data_sugerida: p.data_sugerida ? String(p.data_sugerida).slice(0, 10) : '' })} title="Editar" className={`${btn} text-gray-400 hover:text-blue-600`}><Pencil size={15} /></button>
              <button onClick={() => mudar(p, 'descartada')} title="Descartar" className={`${btn} text-gray-400 hover:text-red-600`}><X size={15} /></button>
              <button onClick={() => excluir(p)} title="Excluir" className={`${btn} text-gray-400 hover:text-red-600`}><Trash2 size={15} /></button>
            </div>
          </div>
          {p.roteiro && <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{p.roteiro}</p>}
        </div>
      ))}
      {!pautas.length && !form && <p className="text-sm text-gray-400">Nenhuma pauta ainda.</p>}
    </div>
  );
}

// ─── Analista de perfil (propõe; você decide) ───────────────────────────────
const NOMES_SUGESTAO = { bio: 'Bio', nome: 'Nome', link: 'Link da bio', destaques: 'Destaques', feed: 'Feed', conteudo: 'Conteúdo', frequencia: 'Frequência', hashtags: 'Hashtags', outro: 'Outro' };
const COR_SUG = { pendente: 'bg-amber-100 text-amber-700', aprovada: 'bg-green-100 text-green-700', aplicada: 'bg-purple-100 text-purple-700', rejeitada: 'bg-gray-100 text-gray-500' };

function CartaoSugestao({ sug, onMudou }) {
  const [txt, setTxt] = useState(sug.proposta);
  const [copiado, setCopiado] = useState(false);
  const decidir = async (status) => { try { await api.post(`/marketing/imagens/sugestoes/${sug.id}/decidir`, { status, proposta: txt !== sug.proposta ? txt : undefined }); onMudou(); } catch (e) { toast.error(msgErro(e)); } };
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wide bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{NOMES_SUGESTAO[sug.tipo] || sug.tipo}</span>
        <p className="font-medium text-gray-800">{sug.titulo}</p>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${COR_SUG[sug.status]}`}>{sug.status}</span>
        {sug.decidido_por && <span className="text-[11px] text-gray-400">por {sug.decidido_por}</span>}
      </div>
      {sug.atual && <p className="text-xs text-gray-500"><b>Hoje:</b> {sug.atual}</p>}
      <textarea className={inp} rows={Math.min(8, Math.max(2, txt.split('\n').length + 1))} value={txt} onChange={(e) => setTxt(e.target.value)} readOnly={sug.status === 'rejeitada'} />
      {sug.justificativa && <p className="text-xs text-gray-500"><b>Por quê:</b> {sug.justificativa}</p>}
      <div className="flex gap-2 flex-wrap">
        <button onClick={async () => { try { await navigator.clipboard.writeText(txt); setCopiado(true); setTimeout(() => setCopiado(false), 1800); } catch { toast.error('Não foi possível copiar'); } }} className={`${btn} text-gray-600 hover:bg-gray-100`}><Copy size={14} /> {copiado ? 'Copiado!' : 'Copiar texto'}</button>
        {sug.status === 'pendente' && (ehGerente()
          ? <><button onClick={() => decidir('aprovada')} className={`${btn} bg-green-600 text-white hover:bg-green-700`}><Check size={14} /> Aprovar{txt !== sug.proposta ? ' (com meus ajustes)' : ''}</button><button onClick={() => decidir('rejeitada')} className={`${btn} text-gray-500 hover:bg-gray-100`}>Rejeitar</button></>
          : <span className="text-xs text-amber-700 self-center">Aguardando aprovação de um gerente</span>)}
        {sug.status === 'aprovada' && ehGerente() && <button onClick={() => decidir('aplicada')} className={`${btn} bg-purple-600 text-white hover:bg-purple-700`}>Marcar como aplicada no perfil</button>}
        {['aprovada', 'aplicada', 'rejeitada'].includes(sug.status) && ehGerente() && <button onClick={() => decidir('pendente')} className={`${btn} text-gray-400 hover:bg-gray-100`}>Reabrir</button>}
      </div>
    </div>
  );
}

function Perfil() {
  const [p, setP] = useState({});
  const [sugestoes, setSugestoes] = useState([]);
  const [anexos, setAnexos] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [analisando, setAnalisando] = useState(false);
  const arq = useRef(null);
  const carregar = useCallback(() => {
    api.get('/marketing/imagens/perfil').then((r) => setP(r.data.data || {})).catch(() => {});
    api.get('/marketing/imagens/sugestoes').then((r) => setSugestoes(r.data.data)).catch(() => {});
  }, []);
  useEffect(() => { carregar(); }, [carregar]);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const salvar = async () => { setSalvando(true); try { const { data } = await api.put('/marketing/imagens/perfil', p); setP(data.data); } catch (e) { toast.error(msgErro(e)); } finally { setSalvando(false); } };
  const anexar = async (files) => {
    for (const f of Array.from(files).slice(0, 4 - anexos.length)) {
      const fd = new FormData(); fd.append('imagem', f);
      try { const { data } = await api.post('/marketing/imagens/anexo', fd); setAnexos((a) => [...a, data.data]); } catch (e) { toast.error(msgErro(e)); }
    }
  };
  const [sincronizando, setSincronizando] = useState(false);
  const sincronizar = async () => {
    setSincronizando(true);
    try { const { data } = await api.post('/marketing/imagens/perfil/sincronizar'); setP(data.data); toast.success('Perfil atualizado pelo Instagram'); }
    catch (e) { toast.error(msgErro(e)); } finally { setSincronizando(false); }
  };
  const analisar = async () => {
    setAnalisando(true);
    try { await api.put('/marketing/imagens/perfil', p); await api.post('/marketing/imagens/perfil/analisar', { anexos }); setAnexos([]); carregar(); }
    catch (e) { toast.error(msgErro(e)); } finally { setAnalisando(false); }
  };
  const an = p.ultima_analise;
  const campo = (k, rotulo, props = {}) => (
    <label className="text-xs text-gray-500 block">{rotulo}
      {props.area ? <textarea className={inp} rows={props.rows || 3} value={p[k] ?? ''} onChange={(e) => set(k, e.target.value)} placeholder={props.ph} />
        : <input className={inp} type={props.num ? 'number' : 'text'} value={p[k] ?? ''} onChange={(e) => set(k, e.target.value)} placeholder={props.ph} />}
    </label>
  );
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500">Analista de perfil: ao analisar, o sistema lê o perfil e o feed direto do Instagram (@, bio, seguidores, curtidas e comentários dos últimos posts); preencha só o contexto e, se quiser, anexe prints do feed. O analista <b>só propõe</b> melhorias de bio, nome, link, destaques, feed e conteúdo — <b>nada é aplicado sem a aprovação de um gerente</b>, e o sistema nunca altera o Instagram: você aplica o que for aprovado.</p>
      <div className="bg-white border border-gray-100 rounded-xl p-4 grid gap-3 md:grid-cols-2">
        {campo('handle', 'Arroba', { ph: '@construtorainbase' })}
        {campo('nome_exibicao', 'Nome exibido no perfil')}
        <div className="md:col-span-2">{campo('bio', 'Bio atual (copie do Instagram)', { area: true })}</div>
        {campo('link_bio', 'Link da bio')}
        <div className="grid grid-cols-3 gap-2">{campo('seguidores', 'Seguidores', { num: true })}{campo('seguindo', 'Seguindo', { num: true })}{campo('posts_total', 'Publicações', { num: true })}</div>
        <div className="md:col-span-2">{campo('destaques', 'Destaques atuais (um por linha)', { area: true, rows: 3 })}</div>
        <div className="md:col-span-2">{campo('observacoes', 'Contexto: objetivo do perfil, público, o que já tentou, o que não pode mudar', { area: true, rows: 3 })}</div>
        <div className="md:col-span-2 flex items-center gap-2 flex-wrap">
          <input ref={arq} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { anexar(e.target.files); e.target.value = ''; }} />
          <button onClick={() => arq.current?.click()} className={`${btn} text-gray-600 hover:bg-gray-100`}><Paperclip size={16} /> Prints do perfil/feed</button>
          {anexos.map((a) => <span key={a.id} className="relative"><img src={a.url} alt="" className="h-12 rounded-md" /><button onClick={() => setAnexos((x) => x.filter((y) => y.id !== a.id))} className="absolute -top-1.5 -right-1.5 bg-gray-700 text-white rounded-full p-0.5"><X size={11} /></button></span>)}
          <button onClick={salvar} disabled={salvando} className={`${btn} text-gray-700 border border-gray-200 hover:bg-gray-50 ml-auto`}>{salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar dados</button>
          <button onClick={sincronizar} disabled={sincronizando} className={`${btn} text-gray-600 hover:bg-gray-100`}>{sincronizando ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={16} />} Atualizar do Instagram</button>
          <button onClick={analisar} disabled={analisando} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>{analisando ? <Loader2 size={14} className="animate-spin" /> : <UserCheck size={16} />} {analisando ? 'Analisando (até 1 min)…' : 'Analisar perfil'}</button>
        </div>
      </div>

      {an && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-2">
          <p className="text-xs text-gray-400">Última análise: {new Date(an.em).toLocaleString('pt-BR')} · {an.modelo}</p>
          <p className="text-sm text-gray-700">{an.resumo}</p>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div><p className="font-medium text-green-700">Pontos fortes</p><ul className="list-disc pl-5 text-gray-600">{(an.pontos_fortes || []).map((x, i) => <li key={i}>{x}</li>)}</ul></div>
            <div><p className="font-medium text-rose-700">Pontos a melhorar</p><ul className="list-disc pl-5 text-gray-600">{(an.pontos_fracos || []).map((x, i) => <li key={i}>{x}</li>)}</ul></div>
          </div>
        </div>
      )}
      <h3 className="font-semibold text-gray-800">Propostas do analista</h3>
      {sugestoes.map((s) => <CartaoSugestao key={`${s.id}-${s.status}`} sug={s} onMudou={carregar} />)}
      {!sugestoes.length && <p className="text-sm text-gray-400">Nenhuma proposta ainda. Preencha os dados e clique em "Analisar perfil".</p>}
    </div>
  );
}

// ─── Conhecimento (memória do agente) ───────────────────────────────────────
const ESCOPOS = [['empreendimento', 'Empreendimento'], ['cidade', 'Cidade (Maringá)'], ['marca', 'Marca / regras']];

function Conhecimento({ empreendimentos }) {
  const [itens, setItens] = useState([]);
  const [form, setForm] = useState(null);
  const carregar = useCallback(() => api.get('/marketing/imagens/conhecimento').then((r) => setItens(r.data.data)).catch((e) => toast.error(msgErro(e))), []);
  useEffect(() => { carregar(); }, [carregar]);
  const salvar = async () => {
    try {
      if (form.id) await api.put(`/marketing/imagens/conhecimento/${form.id}`, form); else await api.post('/marketing/imagens/conhecimento', form);
      setForm(null); carregar();
    } catch (e) { toast.error(msgErro(e)); }
  };
  const excluir = async (i) => { if (!window.confirm(`Excluir "${i.titulo}"?`)) return; try { await api.delete(`/marketing/imagens/conhecimento/${i.id}`); carregar(); } catch (e) { toast.error(msgErro(e)); } };
  const grupos = [...new Set(itens.map((i) => (i.escopo === 'empreendimento' ? i.empreendimento_nome : nomeEscopo(i.escopo))))];
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-gray-500">Memória do agente: localização, entorno (distâncias oficiais), produto, lazer e visual de cada empreendimento, além de Maringá e das regras de veracidade. O agente consulta isto antes de escrever legendas e <b>não afirma nada fora daqui</b> — corrija ou complete sempre que mudar algo.</p>
        <button onClick={() => setForm({ escopo: 'empreendimento', empreendimento_id: '', titulo: '', conteudo: '', fonte: '' })} className={`${btn} bg-blue-600 text-white hover:bg-blue-700 shrink-0`}><Plus size={16} /> Novo</button>
      </div>
      {form && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 grid gap-3">
          <div className="flex gap-2 flex-wrap">
            <select className={`${inp} max-w-[220px]`} value={form.escopo} onChange={(e) => setForm({ ...form, escopo: e.target.value })}>{ESCOPOS.map(([v, n]) => <option key={v} value={v}>{n}</option>)}</select>
            {form.escopo === 'empreendimento' && (
              <select className={`${inp} max-w-[260px]`} value={form.empreendimento_id || ''} onChange={(e) => setForm({ ...form, empreendimento_id: e.target.value })}>
                <option value="">Empreendimento…</option>{empreendimentos.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
              </select>
            )}
          </div>
          <input className={inp} placeholder="Título (ex.: Lumière — localização e entorno)" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} />
          <textarea className={inp} rows={10} placeholder="Fatos verificados, um por linha" value={form.conteudo} onChange={(e) => setForm({ ...form, conteudo: e.target.value })} />
          <input className={inp} placeholder="Fonte (ex.: PDF Apresentação, cadastro do CRM)" value={form.fonte || ''} onChange={(e) => setForm({ ...form, fonte: e.target.value })} />
          <div className="flex gap-2"><button onClick={salvar} className={`${btn} bg-blue-600 text-white`}><Check size={16} /> Salvar</button><button onClick={() => setForm(null)} className={`${btn} text-gray-600`}>Cancelar</button></div>
        </div>
      )}
      {grupos.map((g) => (
        <div key={g} className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">{g}</h3>
          {itens.filter((i) => (i.escopo === 'empreendimento' ? i.empreendimento_nome : nomeEscopo(i.escopo)) === g).map((i) => (
            <div key={i.id} className="bg-white border border-gray-100 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800">{i.titulo}</p>
                  {i.fonte && <p className="text-[11px] text-gray-400">Fonte: {i.fonte}</p>}
                </div>
                <button onClick={() => setForm({ ...i })} title="Editar" className="text-gray-400 hover:text-blue-600"><Pencil size={15} /></button>
                <button onClick={() => excluir(i)} title="Excluir" className="text-gray-400 hover:text-red-600"><Trash2 size={15} /></button>
              </div>
              <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{i.conteudo}</p>
            </div>
          ))}
        </div>
      ))}
      {!itens.length && !form && <p className="text-sm text-gray-400">Nada cadastrado.</p>}
    </div>
  );
}
const nomeEscopo = (e) => (ESCOPOS.find((x) => x[0] === e) || [e, e])[1];

// ─── Página ─────────────────────────────────────────────────────────────────
export default function CriarImagens() {
  const [aba, setAba] = useState('chat');
  const [prefill, setPrefill] = useState('');
  const irParaChat = (texto) => { setPrefill(texto); setAba('chat'); };
  const limparPrefill = useCallback(() => setPrefill(''), []);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  useEffect(() => { api.get('/empreendimentos').then((r) => setEmpreendimentos(r.data.data || [])).catch(() => {}); }, []);
  const abas = [['chat', 'Chat', MessageSquare], ['agenda', 'Agenda de posts', CalendarDays], ['calendario', 'Calendário e pautas', CalendarDays], ['perfil', 'Analista de perfil', UserCheck], ['base', 'Base de imagens', Library], ['temas', 'Temas', Palette], ['conhecimento', 'Conhecimento', BookOpen]];
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2"><ImagePlus size={22} /> Criar Imagens e Social Media</h1>
        <p className="text-sm text-gray-500">Peça uma imagem nova de qualquer empreendimento — ângulo, ambiente, campanha, com a logo — e o agente cria a partir da base. Peça também legendas e cadências de posts prontas para agendar.</p>
      </div>
      <div className="flex gap-1 border-b border-gray-200">
        {abas.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setAba(k)} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${aba === k ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}><Icon size={16} />{l}</button>
        ))}
      </div>
      {aba === 'chat' && <Chat prefill={prefill} onPrefillUsado={limparPrefill} empreendimentos={empreendimentos} />}
      {aba === 'base' && <Base empreendimentos={empreendimentos} />}
      {aba === 'temas' && <Temas />}
      {aba === 'agenda' && <Agenda onAdaptar={(p) => irParaChat(`Adapte o post "${p.titulo || 'sem título'}" (mídia ${(p.midia || []).map((m) => m.id).join(', ')}) para outro formato: crie versões em stories 9:16 e vertical 4:5 mantendo o visual, com legenda adaptada a cada formato.`)} />}
      {aba === 'calendario' && <Calendario empreendimentos={empreendimentos} onCriarNoChat={(p) => irParaChat(`Crie esta pauta${p.tipo === 'reels' ? ' (reels: gere a imagem-chave/primeiro quadro e, se eu pedir, o vídeo)' : ''}: "${p.titulo}". Roteiro: ${p.roteiro || '—'}${p.empreendimento_nome ? ` Empreendimento: ${p.empreendimento_nome}.` : ''} Com legenda.`)} />}
      {aba === 'perfil' && <Perfil />}
      {aba === 'conhecimento' && <Conhecimento empreendimentos={empreendimentos} />}
    </div>
  );
}
